import { DEFAULT_LOGO_URL } from "../constants/generalConstants";

export const OFFICE_HUB_ORG_SAME_AS = [
  "https://www.facebook.com/officehubworkspaces",
  "https://twitter.com/officehubtweet",
  "https://www.pinterest.com/officehubaus",
  "https://www.linkedin.com/company/office-hub-pty-ltd",
  "https://www.youtube.com/channel/UCt4FPeBFmHtejAWDVk0J-4A",
];

interface PostalAddressSchema {
  "@type": "PostalAddress";
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  addressCountry?: string;
  postalCode?: string;
}

interface LdJsonSchema {
  "script:ld+json": {
    "@context": string;
    "@type": string;
    name: string;
    description: string;
    url: string;
    image: string;
    logo?: string;
    telephone?: string;
    address?: PostalAddressSchema;
    sameAs: string[];
  };
}

export function generateOrganizationLdJson(
  baseUrl: string,
  description: string,
  image: string = DEFAULT_LOGO_URL,
  regionConfig?: any,
  listing?: any,
  property?: any,
): LdJsonSchema[] {
  const postalAddress: PostalAddressSchema = { "@type": "PostalAddress" };
  const streetAddress = regionConfig?.address?.streetAddress?.trim() || listing?.addressFormula || listing?.address
  const addressLocality = regionConfig?.address?.addressLocality?.trim() || listing?.city
  const addressRegion = regionConfig?.address?.addressRegion?.trim();
  const addressCountry = (
    regionConfig?.region.toUpperCase()
  ).trim();
  const postalCode = regionConfig?.address?.postalCode?.trim() || listing?.zipcode

  if (streetAddress) postalAddress.streetAddress = streetAddress;
  if (addressLocality) postalAddress.addressLocality = addressLocality;
  if (addressRegion) postalAddress.addressRegion = addressRegion;
  if (addressCountry) postalAddress.addressCountry = addressCountry;
  if (postalCode) postalAddress.postalCode = postalCode;

  const hasAddressFields = Object.keys(postalAddress).length > 1;

  return [
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Office Hub",
        description,
        url: baseUrl || "",
        image: image,
        ...(regionConfig?.phoneNumber?.trim()
          ? { telephone: regionConfig?.phoneNumber.trim() }
          : {}),
        ...(listing?.advertisedPriceMonthly != null || property?.advertisedPriceWeekly != null
          ? {
            priceRange: `${listing?.currencyIsoCode ?? property?.currencyIsoCode
              } ${listing?.advertisedPriceMonthly ?? property?.advertisedPriceWeekly}`,
          }
          : {}),
        ...(hasAddressFields ? { address: postalAddress } : {}),
        sameAs: OFFICE_HUB_ORG_SAME_AS,
      },
    },
  ];
}
import { DomainNameWithSubPathForLocale } from "~/helpers/UrlHelper";
import { getFormattedCountryName } from "./constants";

export interface WorkspaceMetaData {
  workspace: {
    metaTitle?: string;
    name: string;
    billingcity: string;
    metaDescription?: string;
    aboutPartner?: string;
    partnerLogo?: string;
    billingstreet?: string;
    billingstate?: string;
    billingcountry?: string;
    billingpostalcode?: string;
    geolocationLat?: number;
    geolocationLong?: number;
  };
  country: string;
}

export const calculateWorkspaceMetaValues = (
  country: string,
  pathname: string,
) => {
  const title = `Workspace Solutions in ${country}`;
  const description = `Discover flexible workspace solutions in ${country}.`;
  const canonicalUrl = `${DomainNameWithSubPathForLocale(country)}${pathname}`;
  const imageUrl = `${DomainNameWithSubPathForLocale(country)}/assets/office-hub.avif`;

  return { title, description, canonicalUrl, imageUrl };
};

interface Partner {
  name: string;
  partnerLogo?: string;
  aboutPartner?: string;
  totalOnMarketProperties?: number;
}

interface PartnersSchema {
  "@context": string;
  "@type": string;
  name: string;
  itemListElement: {
    "@type": string;
    position: number;
    item: {
      "@type": string;
      name: string;
      aboutPartner?: string;
      aboutPartner?: string;
    };
  }[];
}

export const generatePartnersSchema = (
  partners: Partner[],
  countryName: string,
): PartnersSchema | string => {
  if (!partners || partners.length === 0) {
    return "";
  }

  const schema = {
    "@type": "ItemList",
    name: `Workspace Partners in ${getFormattedCountryName(countryName)}`,
    itemListElement: partners.map((partner, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: partner.name,
      ...(partner.aboutPartner && { description: partner.aboutPartner }),
      ...(partner.partnerLogo && { image: partner.partnerLogo }),
      ...(partner.totalOnMarketProperties && {
        "total properties": partner.totalOnMarketProperties,
      }),
    })),
  };
  return JSON.stringify(schema);
};

interface FAQ {
  question: string;
  answer: string;
}

/** Same-origin document URL without query/hash (canonical shape for JSON-LD @id bases). */
export function normalizeDocumentUrlForSchema(
  requestUrl: string | undefined,
  originFallback: string,
  pathname: string,
): string {
  if (requestUrl && typeof requestUrl === "string") {
    try {
      const u = new URL(requestUrl);
      let href = `${u.origin}${u.pathname}`;
      if (href.length > u.origin.length && href.endsWith("/")) {
        href = href.slice(0, -1);
      }
      return href;
    } catch {
      /* fall through */
    }
  }
  const base =
    originFallback.endsWith("/") && pathname === "/"
      ? originFallback.slice(0, -1)
      : originFallback;
  let href = base + (pathname === "/" ? "" : pathname);
  if (href.length > base.length && href.endsWith("/")) {
    href = href.slice(0, -1);
  }
  return href;
}

export function decodeHtmlEntitiesForSchema(text: string): string {
  return text
    .replace(/&amp;(?:amp;)+#39;/gi, "'")
    .replace(/&amp;#39;/gi, "'")
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&amp;quot;|&quot;/g, '"')
    .replace(/&amp;lt;|&lt;/g, "<")
    .replace(/&amp;gt;|&gt;/g, ">")
    .replace(/&amp;nbsp;|&nbsp;/gi, " ")
    .replace(/&amp;amp;/g, "&")
    .replace(/&amp;/g, "&");
}

/** Plain text for schema `text` fields (strips tags, decodes entities). */
export function stripHtmlToPlainTextForSchema(
  html: string | undefined | null,
  maxLen = 12000,
): string | undefined {
  if (!html || typeof html !== "string") return undefined;
  const withoutTags = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]+>/g, " ");
  const decoded = decodeHtmlEntitiesForSchema(withoutTags);
  const collapsed = decoded.replace(/\s+/g, " ").trim();
  if (!collapsed) return undefined;
  return collapsed.length > maxLen
    ? `${collapsed.slice(0, maxLen - 1).trimEnd()}…`
    : collapsed;
}

export function normalizeIso8601ForSchema(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value
      .replace(/\.000\+0000$/i, "+00:00")
      .replace(/\+0000$/i, "+00:00");
  }
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}+00:00`;
}

/** Maps "5 minutes" / "1 hour" style copy to ISO 8601 duration (e.g. PT5M). */
export function parseReadingTimeToIso8601Duration(
  raw: string | undefined | null,
): string | undefined {
  if (!raw || typeof raw !== "string") return undefined;
  const m = raw.match(/(\d+)\s*(minute|minutes|min|hour|hours|hr|hrs)/i);
  if (!m) return undefined;
  const n = Number.parseInt(m[1], 10);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  const unit = m[2].toLowerCase();
  if (unit.startsWith("hour") || unit === "hr" || unit === "hrs") {
    return `PT${n}H`;
  }
  return `PT${n}M`;
}

export const generateFaqSchema = (
  faqs: FAQ[],
  options?: {
    pageUrl?: string;
    webpageId?: string;
    faqId?: string;
  },
) => {
  if (!faqs || faqs.length === 0) {
    return null;
  }

  const pageUrl = options?.pageUrl;
  const webpageId = options?.webpageId;
  const faqId = options?.faqId;

  const mainEntity = faqs
    .map((faq) => {
      const name = stripHtmlToPlainTextForSchema(faq.question, 500);
      const text = stripHtmlToPlainTextForSchema(faq.answer, 5000);
      if (!name || !text) return null;
      return {
        "@type": "Question",
        name,
        acceptedAnswer: {
          "@type": "Answer",
          text,
        },
      };
    })
    .filter(Boolean);

  if (mainEntity.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(faqId ? { "@id": faqId } : {}),
    ...(pageUrl ? { url: pageUrl } : {}),
    ...(webpageId ? { isPartOf: { "@id": webpageId } } : {}),
    mainEntity,
  };
};

interface BreadcrumbSchemaItem {
  name: string;
  path: string | null;
}

export const generateBreadcrumbSchema = (
  breadcrumbs: BreadcrumbSchemaItem[],
  currentPageUrl: string,
  baseDomain: string,
) => {
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  const normalizedBaseDomain = baseDomain.endsWith("/")
    ? baseDomain.slice(0, -1)
    : baseDomain;

  const itemListElement = breadcrumbs
    .filter((item) => item?.name)
    .map((item, index) => {
      const itemUrl = item.path
        ? `${normalizedBaseDomain}${item.path.startsWith("/") ? "" : "/"}${item.path}`
        : currentPageUrl;

      return {
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: itemUrl,
      };
    });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    name: "Breadcrumbs",
    itemListElement,
  };
};
